import { DatePipe, DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, HostListener, inject, OnInit, signal } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { DatePickerModule } from 'primeng/datepicker';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { GlobalOverlayService } from '../../../core/ui/global-overlay.service';
import { canModifyBooking } from '../../../core/auth/booking-access';
import { AuthService } from '../../../core/auth/auth.service';
import { BOOKING_ACTION_ACCESS, canAccess } from '../../../core/auth/access-control';
import { StatusBadge } from '../../../shared/components/status-badge.component';
import { RefreshButtonComponent } from '../../../shared/components/refresh-button.component';
import { COUNTRY_OPTIONS } from '../../../shared/constants/countries.constant';
import { CustomerService } from '../../customers/services/customer.service';
import { TourItem, TourService } from '../../tours/services/tour.service';
import { BookingListItem, BookingService } from '../services/booking.service';

interface BookingRow {
  id: string;
  tourId: string | null;
  code: string;
  tripName: string;
  tourCode: string;
  country: string;
  countryName: string;
  wholesaleId: string;
  wholesaleName: string;
  customerName: string;
  customerCode: string;
  total: number;
  bookingStatus: BookingListItem['status'];
  paymentStatus: BookingListItem['paymentStatus'];
  travelDate: string;
  returnDate: string | null;
  customerId: string;
  travelerCount: number;
  tourPrice: number;
  discount: number;
  estimatedExpense: number;
  estimatedExpenseTotal: number;
  hasPayments: boolean;
  companyCommission: number;
  employeeCommission: number;
  specialCommission: number;
}

@Component({
  selector: 'app-bookings-page',
  imports: [
    DatePipe,
    DecimalPipe,
    ReactiveFormsModule,
    LucideAngularModule,
    StatusBadge,
    RefreshButtonComponent,
    TableModule,
    InputTextModule,
    SelectModule,
    DatePickerModule,
    CheckboxModule,
    InputNumberModule,
  ],
  templateUrl: './bookings-page.component.html',
})
export class BookingsPageComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  readonly isSavingDomesticEdit = signal(false);
  readonly domesticEditForm = this.fb.nonNullable.group({
    customerId: ['', Validators.required],
    travelerCount: [1, [Validators.required, Validators.min(1), Validators.pattern(/^\d+$/)]],
    discount: [0, [Validators.required, Validators.min(0)]],
  });

  private readonly overlay = inject(GlobalOverlayService);
  private readonly router = inject(Router);
  private readonly customerService = inject(CustomerService);
  private readonly tourService = inject(TourService);
  private readonly bookingService = inject(BookingService);
  private readonly auth = inject(AuthService);

  readonly canManageBooking = computed(() =>
    canAccess(this.auth.user(), BOOKING_ACTION_ACCESS.manage),
  );
  readonly isAdmin = computed(() => this.auth.user()?.role.level === 1);

  readonly bookings = signal<BookingRow[]>([]);
  readonly isBookingModalOpen = signal(false);
  readonly editingDomesticBooking = signal<BookingRow | null>(null);
  readonly bookingItems = signal<BookingListItem[]>([]);
  readonly editingDomesticBookingItem = computed(() =>
    this.bookingItems().find((booking) => booking.id === this.editingDomesticBooking()?.id) ?? null,
  );
  readonly bookingModalType = signal<'external' | 'domestic'>('external');
  readonly isLoadingBookings = signal(false);
  readonly isSaving = signal(false);
  readonly editingBookingId = signal<string | null>(null);
  readonly customerOptions = signal<Array<{ label: string; value: string }>>([]);

  readonly wholesaleOptions = signal<Array<{ label: string; value: string }>>([]);

  readonly domesticTourCodeOptions = signal<Array<{ id: string; label: string; value: string }>>([]);
  readonly selectedDomesticTour = signal<TourItem | null>(null);
  readonly isLoadingDomesticTour = signal(false);

  readonly countryOptions = COUNTRY_OPTIONS.map((country) => ({
    label: country.label,
    value: country.label,
  }));

  ngOnInit(): void {
    void this.loadPageData();
  }

  async loadPageData(): Promise<void> {
    if (this.isLoadingBookings()) return;
    this.isLoadingBookings.set(true);
    this.overlay.showLoading('กำลังโหลดข้อมูล Booking...');
    try {
      const [customers, wholesales, bookings, tours] = await Promise.all([
        this.canManageBooking() ? this.customerService.getActiveOptions() : Promise.resolve([]),
        this.bookingService.getWholesales(),
        this.bookingService.getAll('JOIN_TOUR'),
        this.tourService.getAll(),
      ]);
      this.customerOptions.set(
        customers.map((customer) => ({ label: customer.name, value: customer.id })),
      );
      this.wholesaleOptions.set(
        wholesales.map((wholesale) => ({ label: wholesale.name, value: wholesale.id })),
      );
      this.setDomesticTourCodeOptions(tours);
      this.bookingItems.set(bookings);
      this.bookings.set(
        bookings.map((booking) => ({
          id: booking.id,
          tourId: booking.tourId,
          code: booking.bookingNo,
          tripName: booking.tourName,
          tourCode: booking.tourCode ?? '',
          country:
            COUNTRY_OPTIONS.find((country) => country.value === booking.country)?.label ??
            booking.country ??
            '',
          countryName:
            COUNTRY_OPTIONS.find((country) => country.value === booking.country)?.label ??
            booking.country ??
            '-',
          wholesaleId: booking.wholesaleId ?? '',
          wholesaleName: booking.booking_wholesale?.name ?? '-',
          customerName: booking.booking_customer.name,
          customerCode: booking.booking_customer.customerCode,
          total: Number(booking.totalAmount),
          bookingStatus: booking.status,
          paymentStatus: booking.paymentStatus,
          travelDate: booking.departureDate,
          returnDate: booking.returnDate,
          customerId: booking.booking_customer.id,
          travelerCount: booking.pax,
          tourPrice: Number(booking.pricePerPerson),
          discount: Number(booking.discount ?? 0),
          estimatedExpense: Number(booking.estimatedExpense ?? 0) / Math.max(booking.pax, 1),
          estimatedExpenseTotal: Number(booking.estimatedExpense ?? 0),
          hasPayments: booking.booking_payments.length > 0,
          companyCommission: Number(booking.com_company ?? 0) / Math.max(booking.pax, 1),
          employeeCommission: Number(booking.com_sale ?? 0) / Math.max(booking.pax, 1),
          specialCommission: Number(booking.com_special ?? 0) / Math.max(booking.pax, 1),
        })),
      );
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'กรุณาลองใหม่อีกครั้ง';
      this.overlay.error('โหลดข้อมูล Booking ไม่สำเร็จ', message);
    } finally {
      this.isLoadingBookings.set(false);
      this.overlay.hideLoading();
    }
  }

  readonly bookingForm = this.fb.group(
    {
      customerId: ['', Validators.required],
      customerIsTraveler: [false],
      tourCode: ['', Validators.required],
      wholesalerId: ['', Validators.required],
      programName: ['', Validators.required],
      country: ['', Validators.required],
      departureDate: [null as Date | null, Validators.required],
      returnDate: [null as Date | null, Validators.required],
      travelerCount: [1, [Validators.required, Validators.min(1)]],
      tourPrice: [null as number | null, [Validators.required, Validators.min(0)]],
      discount: [0 as number | null, [Validators.min(0)]],
      estimatedExpense: [null as number | null, [Validators.required, Validators.min(0)]],
      companyCommission: [null as number | null, [Validators.required, Validators.min(0)]],
      employeeCommission: [null as number | null, [Validators.required, Validators.min(0)]],
      specialCommission: [null as number | null, [Validators.required, Validators.min(0)]],
    },
    { validators: this.estimatedExpenseLimitValidator() },
  );

  private estimatedExpenseLimitValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const travelerCount = Number(control.get('travelerCount')?.value);
      const tourPrice = Number(control.get('tourPrice')?.value);
      const discount = Number(control.get('discount')?.value ?? 0);
      const estimatedExpense = Number(control.get('estimatedExpense')?.value);

      if (
        !Number.isFinite(travelerCount) ||
        !Number.isFinite(tourPrice) ||
        !Number.isFinite(discount) ||
        !Number.isFinite(estimatedExpense)
      ) {
        return null;
      }

      const grossBookingTotal = tourPrice * travelerCount;
      const netBookingTotal = Math.max(grossBookingTotal - discount, 0);
      const estimatedExpenseTotal = estimatedExpense * travelerCount;
      const errors: ValidationErrors = {};
      if (discount > grossBookingTotal) errors['discountExceeded'] = true;
      if (estimatedExpenseTotal > netBookingTotal) {
        errors['estimatedExpenseExceeded'] = true;
      }
      return Object.keys(errors).length ? errors : null;
    };
  }

  netBookingTotal(): number {
    const { tourPrice, discount } = this.bookingForm.getRawValue();
    return Math.max(
      (tourPrice ?? 0) - (discount ?? 0),
      0,
    );
  }

  openBookingModal(): void {
    this.bookingModalType.set('external');
    this.editingBookingId.set(null);
    this.bookingForm.reset({
      travelerCount: 1,
      customerIsTraveler: false,
      discount: 0,
      companyCommission: null,
      employeeCommission: null,
      specialCommission: null,
    });
    this.isBookingModalOpen.set(true);
  }

  openEditBookingModal(booking: BookingRow): void {
    if (booking.bookingStatus === 'DELETED' || !this.canModifyBooking(booking)) return;
    if (booking.tourId) {
      this.openEditDomesticBookingModal(booking);
      return;
    }
    this.bookingModalType.set('external');
    this.editingBookingId.set(booking.id);
    this.bookingForm.reset({
      customerId: booking.customerId,
      customerIsTraveler: false,
      tourCode: booking.tourCode,
      wholesalerId: booking.wholesaleId,
      programName: booking.tripName,
      country: booking.country,
      departureDate: this.parseDate(booking.travelDate),
      returnDate: booking.returnDate ? this.parseDate(booking.returnDate) : null,
      travelerCount: booking.travelerCount,
      tourPrice: booking.tourPrice,
      discount: booking.discount,
      estimatedExpense: booking.estimatedExpense,
      companyCommission: booking.companyCommission,
      employeeCommission: booking.employeeCommission,
      specialCommission: booking.specialCommission,
    });
    this.isBookingModalOpen.set(true);
  }

  closeBookingModal(): void {
    this.isBookingModalOpen.set(false);
    this.editingBookingId.set(null);
  }

  openEditDomesticBookingModal(booking: BookingRow): void {
    if (!booking.tourId || booking.bookingStatus === 'DELETED' || !this.canModifyBooking(booking)) return;
    const item = this.bookingItems().find((item) => item.id === booking.id);
    if (!item) return;
    this.domesticEditForm.reset({
      customerId: item.booking_customer.id,
      travelerCount: item.pax,
      discount: Number(booking.discount ?? 0),
    });
    this.editingDomesticBooking.set(booking);
  }

  closeEditDomesticBookingModal(): void {
    if (this.isSavingDomesticEdit()) return;
    this.editingDomesticBooking.set(null);
  }

  closeDomesticEditModal(): void {
    this.closeEditDomesticBookingModal();
  }

  async saveDomesticBookingChanges(): Promise<void> {
    const booking = this.editingDomesticBookingItem();
    if (!booking) return;
    if (this.isSavingDomesticEdit() || !booking.tourId || booking.status === 'DELETED' ||
        !canModifyBooking(this.auth.user(), booking.booking_payments.length > 0)) return;
    this.domesticEditForm.markAllAsTouched();
    if (this.domesticEditForm.invalid) return;
    const value = this.domesticEditForm.getRawValue();
    if (value.discount > Number(booking.pricePerPerson) * value.travelerCount) {
      this.domesticEditForm.controls.discount.setErrors({ discountExceeded: true });
      return;
    }
    this.isSavingDomesticEdit.set(true);
    this.overlay.showLoading('กำลังบันทึกการจองทัวร์ภายใน...');
    try {
      await this.bookingService.updateDomestic(booking.id, {
        customerId: value.customerId, pax: value.travelerCount, discount: value.discount,
      });
      this.overlay.success('แก้ไขการจองทัวร์ภายในสำเร็จ', `บันทึก ${booking.bookingNo} เรียบร้อยแล้ว`);
      this.editingDomesticBooking.set(null);
      await this.loadPageData();
    } catch (error: unknown) {
      const message = error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
        ? error.error.message : 'กรุณาลองใหม่อีกครั้ง';
      this.overlay.error('แก้ไขการจองทัวร์ภายในไม่สำเร็จ', message);
    } finally {
      this.isSavingDomesticEdit.set(false);
      this.overlay.hideLoading();
    }
  }

  async openDomesticJoinModal(): Promise<void> {
    this.openBookingModal();
    this.bookingModalType.set('domestic');
    this.selectedDomesticTour.set(null);
    this.domesticTourCodeOptions.set([]);
    this.isLoadingDomesticTour.set(true);
    try {
      this.setDomesticTourCodeOptions(await this.tourService.getAll());
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'ไม่สามารถโหลดรายการทัวร์ได้';
      this.overlay.error('โหลดรายการทัวร์ไม่สำเร็จ', message);
    } finally {
      this.isLoadingDomesticTour.set(false);
    }
  }

  async onDomesticTourSelected(tourCode: string | null): Promise<void> {
    this.selectedDomesticTour.set(null);
    if (!tourCode) return;

    const option = this.domesticTourCodeOptions().find((item) => item.value === tourCode);
    if (!option) return;

    this.isLoadingDomesticTour.set(true);
    try {
      this.selectedDomesticTour.set(await this.tourService.getById(option.id));
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'ไม่สามารถโหลดข้อมูลทัวร์ได้';
      this.overlay.error('โหลดข้อมูลทัวร์ไม่สำเร็จ', message);
    } finally {
      this.isLoadingDomesticTour.set(false);
    }
  }

  isInvalid(controlName: keyof typeof this.bookingForm.controls): boolean {
    const control = this.bookingForm.controls[controlName];
    return control.touched && control.invalid;
  }

  domesticTourRemainingPax(): number | null {
    const tour = this.selectedDomesticTour();
    if (!tour || tour.requiredPax === null) return null;

    const bookedPax = this.domesticTourBookedPax(tour);
    const currentPax = Number(this.bookingForm.controls.travelerCount.value ?? 0);
    return Math.max(tour.requiredPax - bookedPax - currentPax, 0);
  }

  domesticTourCapacityExceeded(): boolean {
    const tour = this.selectedDomesticTour();
    if (!tour || tour.requiredPax === null) return false;

    const bookedPax = this.domesticTourBookedPax(tour);
    const currentPax = Number(this.bookingForm.controls.travelerCount.value ?? 0);
    return bookedPax + currentPax > tour.requiredPax;
  }

  private domesticTourBookedPax(tour: TourItem): number {
    return tour.bookings
      .filter((booking) => !['DELETED', 'CANCELLED', 'REFUND'].includes(booking.status))
      .reduce((total, booking) => total + booking.pax, 0);
  }

  private setDomesticTourCodeOptions(tours: TourItem[]): void {
    this.domesticTourCodeOptions.set(
      tours
        .filter((tour) => {
          if (tour.tourType !== 'JOIN_TOUR') return false;
          if (!['DRAFT', 'ACTIVE'].includes(tour.status)) return false;
          if (tour.requiredPax === null) return true;
          return this.domesticTourBookedPax(tour) < tour.requiredPax;
        })
        .map((tour) => ({
          id: tour.id,
          label: `${tour.tourCode} · ${tour.tourName}`,
          value: tour.tourCode,
        })),
    );
  }

  async saveBooking(): Promise<void> {

    if (this.bookingModalType() === 'domestic') {
      await this.saveDomesticBooking();
      return;
    }

    if (this.bookingForm.invalid) {
      this.bookingForm.markAllAsTouched();
      return;
    }

    const { departureDate, returnDate } = this.bookingForm.getRawValue();
    if (departureDate && returnDate && returnDate < departureDate) {
      this.overlay.error('วันที่เดินทางไม่ถูกต้อง', 'วันเดินทางกลับต้องไม่น้อยกว่าวันเดินทางไป');
      return;
    }

    const formValue = this.bookingForm.getRawValue();
    const bookingDetail = {
      bookingType: 'JOIN_TOUR' as const,
      customerName:
        this.customerOptions().find((option) => option.value === formValue.customerId)?.label ??
        '-',
      tourCode: formValue.tourCode ?? '-',
      wholesalerName:
        this.wholesaleOptions().find((option) => option.value === formValue.wholesalerId)?.label ??
        '-',
      programName: formValue.programName ?? '-',
      countryName:
        this.countryOptions.find((option) => option.value === formValue.country)?.label ??
        formValue.country ??
        '-',
      departureDate: formValue.departureDate,
      returnDate: formValue.returnDate,
      travelerCount: formValue.travelerCount,
      tourPrice: formValue.tourPrice,
      discount: formValue.discount,
      estimatedExpense: formValue.estimatedExpense,
      companyCommission: formValue.companyCommission,
      employeeCommission: formValue.employeeCommission,
      specialCommission: formValue.specialCommission,
    };

    this.isSaving.set(true);
    const editingBookingId = this.editingBookingId();
    this.overlay.showLoading(editingBookingId ? 'กำลังแก้ไข Booking...' : 'กำลังสร้าง Booking...');
    try {
      const request = {
        bookingType: 'JOIN_TOUR' as const,
        customerId: formValue.customerId!,
        customerIsTraveler: formValue.customerIsTraveler ?? false,
        tourName: formValue.programName!,
        tourCode: formValue.tourCode!,
        country: formValue.country!,
        wholesaleId: formValue.wholesalerId!,
        departureDate: this.formatDate(formValue.departureDate!),
        returnDate: this.formatDate(formValue.returnDate!),
        pax: formValue.travelerCount!,
        tourPrice: formValue.tourPrice!,
        discount: formValue.discount ?? 0,
        totalAmount:
          formValue.tourPrice! * formValue.travelerCount! - (formValue.discount ?? 0),
        estimatedExpense: formValue.estimatedExpense ?? 0,
        companyCommission: formValue.companyCommission ?? 0,
        employeeCommission: formValue.employeeCommission ?? 0,
        specialCommission: formValue.specialCommission ?? 0,
      };
      if (editingBookingId) {
        await this.bookingService.update(editingBookingId, request);
        this.closeBookingModal();
        await this.loadPageData();
        this.overlay.success('แก้ไข Booking สำเร็จ', 'อัปเดตข้อมูล Booking เรียบร้อยแล้ว');
        return;
      }

      const booking = await this.bookingService.create(request);
      this.closeBookingModal();
      this.overlay.success('สร้าง Booking สำเร็จ', `บันทึก ${booking.bookingNo} เรียบร้อยแล้ว`);
      void this.router.navigate(['/bookings', booking.id], {
        queryParams: { from: 'bookings' },
        state: { booking: bookingDetail },
      });
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'ไม่สามารถสร้าง Booking ได้';
      this.overlay.error('สร้าง Booking ไม่สำเร็จ', message);
    } finally {
      this.isSaving.set(false);
    }
  }

  private async saveDomesticBooking(): Promise<void> {
    const formValue = this.bookingForm.getRawValue();
    const selectedTour = this.selectedDomesticTour();
    const requiredControls = [
      this.bookingForm.controls.customerId,
      this.bookingForm.controls.tourCode,
      this.bookingForm.controls.travelerCount,
    ];

    if (
      !selectedTour ||
      requiredControls.some((control) => control.invalid) ||
      this.domesticTourCapacityExceeded()
    ) {
      requiredControls.forEach((control) => control.markAsTouched());
      return;
    }

    this.isSaving.set(true);
    this.overlay.showLoading('กำลังบันทึกจอยทัวร์ภายใน...');
    try {
      const booking = await this.bookingService.createDomestic({
        customerId: formValue.customerId!,
        tourId: selectedTour.id,
        customerIsTraveler: formValue.customerIsTraveler ?? false,
        pax: formValue.travelerCount ?? 1,
        discount: formValue.discount ?? 0,
      });
      this.closeBookingModal();
      await this.loadPageData();
      this.overlay.success('สร้างจอยทัวร์ภายในสำเร็จ', `บันทึก ${booking.bookingNo} เรียบร้อยแล้ว`);
      void this.router.navigate(['/bookings', booking.id], {
        queryParams: { from: 'bookings' },
      });
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'ไม่สามารถสร้างจอยทัวร์ภายในได้';
      this.overlay.error('สร้างจอยทัวร์ภายในไม่สำเร็จ', message);
    } finally {
      this.isSaving.set(false);
      this.overlay.hideLoading();
    }
  }

  async deleteBooking(booking: BookingRow): Promise<void> {
    if (booking.bookingStatus === 'DELETED' || !this.canModifyBooking(booking)) return;
    const confirmed = await this.overlay.confirm({
      title: 'ยืนยันการลบ Booking',
      message: `ต้องการลบ ${booking.code} ใช่หรือไม่? รายการจะถูกเปลี่ยนสถานะเป็นลบแล้ว`,
      confirmText: 'ลบ Booking',
      cancelText: 'ยกเลิก',
    });
    if (!confirmed) return;

    this.overlay.showLoading('กำลังลบ Booking...');
    try {
      const deletedBooking = await this.bookingService.delete(booking.id);
      this.bookings.update((bookings) =>
        bookings.map((item) =>
          item.id === deletedBooking.id
            ? { ...item, bookingStatus: deletedBooking.status }
            : item,
        ),
      );
      this.overlay.success('ลบ Booking สำเร็จ', 'เปลี่ยนสถานะ Booking เป็นลบแล้ว');
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'ไม่สามารถลบ Booking ได้';
      this.overlay.error('ลบ Booking ไม่สำเร็จ', message);
    }
  }

  private parseDate(value: string): Date {
    return new Date(`${value.slice(0, 10)}T00:00:00`);
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  viewBooking(id: string): void {
    void this.router.navigate(['/bookings', id], { queryParams: { from: 'bookings' } });
  }

  canModifyBooking(booking: BookingRow): boolean {
    return canModifyBooking(this.auth.user(), booking.hasPayments);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.editingDomesticBooking()) {
      this.closeDomesticEditModal();
      return;
    }
    if (this.isBookingModalOpen()) {
      this.closeBookingModal();
    }
  }
}
